/**
 * useAuth hook. Thin wrapper around the Zustand auth store plus
 * TanStack Query mutations for signup/login/logout. Components that
 * don't need the mutations can use the store directly instead.
 */
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore.ts";
import { authApi } from "@/api/auth.ts";
import { attributionApi } from "@/api/attribution.ts";
import { referralsApi } from "@/api/referrals.ts";
import { getFullAttribution } from "@/hooks/useUtm.ts";
import { getStoredReferralCode, clearReferralCode } from "@/hooks/useReferral.ts";
import type { LoginRequest, SignupRequest } from "@vms/tubeforge-shared";

export function useAuth() {
  const navigate = useNavigate();
  const { user, token, setAuth, clearAuth, isAuthenticated } = useAuthStore();

  const signup = useMutation({
    mutationFn: (body: SignupRequest) => authApi.signup(body),
    onSuccess: (data, variables) => {
      setAuth({
        user: data.user,
        token: data.token,
        expiresAt: data.expiresAt,
      });

      // Fire-and-forget: send attribution data now that we have a token
      const attrs = getFullAttribution();
      if (Object.keys(attrs).length > 0) {
        attributionApi.track(attrs).catch(() => {});
      }

      // Fire-and-forget: record referral if user came via a referral link
      const refCode = getStoredReferralCode();
      if (refCode) {
        referralsApi.recordSignup(refCode).catch(() => {});
        clearReferralCode();
      }

      // Navigate to OTP page for email verification
      navigate("/otp", { state: { email: variables.email, verify: true } });
    },
  });

  const login = useMutation({
    mutationFn: (body: LoginRequest) => authApi.login(body),
    onSuccess: (data) => {
      setAuth({
        user: data.user,
        token: data.token,
        expiresAt: data.expiresAt,
      });
      navigate("/dashboard");
    },
  });

  const logout = (): void => {
    clearAuth();
    navigate("/login");
  };

  return {
    user,
    token,
    isAuthenticated: isAuthenticated(),
    signup,
    login,
    logout,
  };
}
